import React, {useEffect, useState} from "react";

import Box from "@mui/material/Box";

import "../App.css"
import {PageHeader} from "./PageHeader";

import {useAppCtx} from "../app/AppCtx";
import {useCodeCtx} from "../app/CodeCtx";
import {useCrewCtx} from "../app/CrewCtx";

import {CxGrid} from "../cmx/CxGrid";
import {CxTitle, CxSubtitle, CxText, CxBoldText, CxSmallText, CxSection, CxCaption} from "../cmx/CxTypography";
import {CxStatusIcon} from "../cmx/CxIcon";
import {CxStatusColor} from "../cmx/CxColor";
import {CxButton} from "../cmx/CxForm";
import {CxFormFeedback} from "../cmx/CxFeedback";
import {CxDataGrid} from "../cmx/CxData";
import {overallStatus} from "../ctx/AppStatus";
import {accessCtrl} from "../ctx/AccessCtrl";
import {Operator} from "../domain/Operator";
import {initialCall, runningCall, successCall} from "../gateway/CallService";
import {operDDL, bootstrapList, operBootstrapDDL} from "../gateway/OperService";
import {DateUtil} from "../lib/DateUtil";
import buildinfo from '../res/buildinfo.json';

// ----------------------------------------------------------------------------------------------------------------
// header
// ----------------------------------------------------------------------------------------------------------------

const actionItems = [
    { kind: "tooltip", label: "Status ",    iconName: "status",  actionName: "operStatus" },
    { kind: "tooltip", label: "Context",    iconName: "context", actionName: "operContext" },
    { kind: "tooltip", label: "Cache",      iconName: "cache",   actionName: "operCache" },
    { kind: "tooltip", label: "Typography", iconName: "typo",    actionName: "operTypo" }
];

const renderHeader = (title) => {
    return (
        <PageHeader title={title}  actionItems={actionItems} />
    );
}

// ----------------------------------------------------------------------------------------------------------------
// OperStatus
// ----------------------------------------------------------------------------------------------------------------

export function OperStatus() {
    const {appStatus} = useAppCtx();
    const status = overallStatus(appStatus);
    const color = CxStatusColor[status];


    return (
        <Box sx={{ width: '100%' }}>
            <CxGrid container spacing={2}>
                { renderHeader("Operation Status") }

                <CxGrid size="xl">
                    <CxSubtitle>Overall</CxSubtitle>
                </CxGrid>
                <CxGrid size="xl">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CxStatusIcon status={status} />
                        <Box sx={{ ml: 1 }}>
                            <CxText sx={{ color: color }} >{status}</CxText>
                        </Box>
                    </Box>
                </CxGrid>

                <CxGrid size="xl">
                    <CxSubtitle>Details</CxSubtitle>
                </CxGrid>
                <CxGrid size="xl">
                    <pre>{JSON.stringify(appStatus, null, 2)}</pre>
                </CxGrid>

            </CxGrid>
        </Box>
    );
}

// ----------------------------------------------------------------------------------------------------------------
// OperContext
// ----------------------------------------------------------------------------------------------------------------

export function OperContext() {
    const {pageSettings, user, isLoggedIn} = useAppCtx();

    return (
        <Box sx={{ width: '100%' }}>
            <CxGrid container spacing={2}>
                { renderHeader("Operation Context") }

                <CxGrid size="xl">
                    <CxSubtitle>User</CxSubtitle>
                </CxGrid>
                <CxGrid size="xl">
                    <pre>{JSON.stringify(user, null, 2)}</pre>
                </CxGrid>
                <CxGrid size="xl">
                    <pre>isLoggedIn: {String(isLoggedIn)}</pre>
                </CxGrid>

                <CxGrid size="xl">
                    <CxSubtitle>Access Control</CxSubtitle>
                </CxGrid>
                <CxGrid size="xl">
                    <pre>{JSON.stringify(accessCtrl, null, 2)}</pre>
                </CxGrid>

                <CxGrid size="xl">
                    <CxSubtitle>Page Settings</CxSubtitle>
                </CxGrid>
                <CxGrid size="xl">
                    <pre>{JSON.stringify(pageSettings, null, 2)}</pre>
                </CxGrid>

                <CxGrid size="xl">
                    <CxSubtitle>BuildInfo</CxSubtitle>
                </CxGrid>
                <CxGrid size="xl">
                    <pre>{JSON.stringify(buildinfo, null, 2)}</pre>
                </CxGrid>

            </CxGrid>
        </Box>
    );
}

// ----------------------------------------------------------------------------------------------------------------
// OperCache
// ----------------------------------------------------------------------------------------------------------------

export function OperCache() {
    const {code, lazyloadCodes, loadCodes, codeLoadCount, codeLoadTs} = useCodeCtx();
    const {crew, lazyloadCrews, loadCrews, crewLoadCount, crewLoadTs} = useCrewCtx();

    useEffect(() => {
        lazyloadCodes();
        lazyloadCrews();
    }, []); // Runs only on mount

    const codeCount = code.getCodes().length;
    const crewCount = crew.getCrews().length;
    const teamCount = crew.getTeams().length;

    const handleRefreshCodes = () => {
        loadCodes();
    };

    const handleRefreshCrews = () => {
        loadCrews();
    };

    return (
        <Box sx={{ width: '100%' }}>
            <CxGrid container spacing={2}>
                { renderHeader("Operation Cache") }

                <CxGrid size="xl">
                    <CxSubtitle>Codes (taks, subtask, tool)</CxSubtitle>
                </CxGrid>
                <CxGrid size="lg">
                    Codes loaded: {codeCount} | Load count: {codeLoadCount} | Load timestamp: {DateUtil.convertDateTimeToIsoString(codeLoadTs)}
                </CxGrid>
                <CxGrid size="lg"><button onClick={handleRefreshCodes}>Refresh Codes</button></CxGrid>

                <CxGrid size="xl">
                    <CxSubtitle>Crews and Teams (are loaded together)</CxSubtitle>
                </CxGrid>
                <CxGrid size="lg">
                    Crews loaded: {crewCount} | Load count: {crewLoadCount} | Load timestamp: {DateUtil.convertDateTimeToIsoString(crewLoadTs)}
                </CxGrid>
                <CxGrid size="lg"><button onClick={handleRefreshCrews}>Refresh Crews</button></CxGrid>

                <CxGrid size="lg">
                    Teams loaded: {teamCount} | Load count: {crewLoadCount} | Load timestamp: {DateUtil.convertDateTimeToIsoString(crewLoadTs)}
                </CxGrid>
                <CxGrid size="lg"><button onClick={handleRefreshCrews}>Refresh Teams</button></CxGrid>

            </CxGrid>
        </Box>
    );
}

// ----------------------------------------------------------------------------------------------------------------
// OperDDL
// ----------------------------------------------------------------------------------------------------------------

export function OperDDL() {

    const [call, setCall] = useState(initialCall())
    const [ddlMap, setDdlMap] = useState([])
    const [bootstrapRows, setBootstrapRows] = useState([])
    const [bootstrapDdlResult, setBootsrapDDLResult] = useState([])

    const loadOperDDL = async () => {
        setCall(runningCall());
        const responseCall = await operDDL();

        if (responseCall.isSuccess) {
            setDdlMap(responseCall.result)
            setCall(successCall());
        } else {
            setDdlMap([])
            setCall(responseCall);
        }
    };

    const loadBootstrapList = async () => {
        setCall(runningCall());
        const responseCall = await bootstrapList();

        if (responseCall.isSuccess) {
            const sortedRows = [...responseCall.result].sort(
                (a, b) => a.value.localeCompare(b.value)
            );
            setBootstrapRows(sortedRows)
            setCall(successCall());
        } else {
            setBootstrapRows([])
            setCall(responseCall);
        }
    };

    // initial load
    useEffect(() => {
        loadOperDDL();
        loadBootstrapList();
    }, []);


    const handleSubmit = async (action) => {
        setCall(runningCall);
        const responseCall = await operBootstrapDDL()

        if (responseCall.isSuccess) {
            setBootsrapDDLResult(responseCall.result)
            await loadOperDDL();
            await loadBootstrapList();
        } else {
            setCall(responseCall);
        }
    }

    return (
        <Box sx={{ width: '100%' }}>
            <CxGrid container spacing={2}>

                { renderHeader("Operation DDL") }

                <CxSubtitle>Availabel DDL Scripts</CxSubtitle>
                <CxGrid size="xl">
                    <ol start={0} style={{ paddingLeft: "1rem", lineHeight: "1.5" }}>
                        {ddlMap.map((name) => (
                            <li key={name} style={{ paddingLeft: "0.6rem" }}>{name}</li>
                        ))}
                    </ol>
                </CxGrid>

                <CxSubtitle>Applied DDL Scripts</CxSubtitle>
                <CxGrid size="xl">
                    <CxDataGrid
                        rows={bootstrapRows}
                        columns={Operator.getBootstrapColumns()}
                        getRowId={(row) => row.key}
                    />
                </CxGrid>

                <CxSubtitle>Bootstrap DDL Scripts</CxSubtitle>
                <CxGrid size="xl" sx={{ display: 'flex', justifyContent: 'flex-start' }} >
                    <CxButton label="Apply new scripts" action="bootstrap" handleSubmit={handleSubmit} />
                </CxGrid>
                <CxGrid size="xl">
                    <CxText>Last executed scripts:</CxText>
                    <CxText>{JSON.stringify(bootstrapDdlResult, null, 2)}</CxText>
                </CxGrid>
                <CxFormFeedback call={call} />

            </CxGrid>
        </Box>
    );
}


// ----------------------------------------------------------------------------------------------------------------
// OperTypo
// ----------------------------------------------------------------------------------------------------------------

export function OperTypo() {
    return (
        <Box sx={{ width: '100%' }}>
            <CxGrid container spacing={2}>

                { renderHeader("Operation Typography") }

                <CxGrid size="xl">
                    <CxTitle>Title</CxTitle>
                </CxGrid>

                <CxGrid size="xl">
                    <CxSubtitle>Subitle</CxSubtitle>
                </CxGrid>

                <CxGrid size="xl">
                    <CxSection>Section</CxSection>
                </CxGrid>

                <CxGrid size="xl">
                    <CxCaption>Caption</CxCaption>
                </CxGrid>

                <CxGrid size="xl">
                    <CxBoldText>Bold Text</CxBoldText>
                </CxGrid>

                <CxGrid size="xl">
                    <CxText>Text</CxText>
                </CxGrid>

                <CxGrid size="xl">
                    <CxSmallText>Small Text</CxSmallText>
                </CxGrid>

            </CxGrid>
        </Box>
    );
}

// --------------------------------------------------------------------------------------------------------------------
// The end.